import hubspotIcon from "@/assets/integrations/hubspot.svg";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useHubSpotIntegration } from "@/contexts/hubspot-integration-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { CompanyDTO, HubSpotSaveToListSyncedRecordDTO } from "@shaarp/common";
import { ChevronsUpDownIcon, DownloadIcon, FileSpreadsheetIcon, Loader2Icon } from "lucide-react";
import { useState } from "react";
import { useToast, useToastError } from "../ui/use-toast";
import { CompaniesHubspotListExport } from "./companies-hubspot-list-export";

interface DataTableToolbarCompaniesExportProps {
  companies: CompanyDTO[];
  fetchDataLoading: boolean;
  onCompaniesSynced?: (syncedRecords: HubSpotSaveToListSyncedRecordDTO[]) => void;
}

export function DataTableToolbarCompaniesExport({
  companies,
  fetchDataLoading,
  onCompaniesSynced,
}: DataTableToolbarCompaniesExportProps) {
  const toastError = useToastError();
  const api = useApi();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { hubspotIntegration } = useHubSpotIntegration();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hubspotDialogOpen, setHubspotDialogOpen] = useState(false);

  const exportCompanies = async (format: "csv" | "xlsx") => {
    const companiesIds = companies.map((c) => c.id);

    if (companiesIds.length === 0) {
      toastError();
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post<{ data: number[] }>(`/companies/export`, {
        companiesIds,
        format,
      });

      const byteArray = new Uint8Array(response.data.data);
      const blob = new Blob([byteArray], {
        type:
          format === "csv"
            ? "text/csv"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const fileName = `shaarp_export_${
        companiesIds.length
      }_societes_${new Date().toLocaleString()}.${format}`;
      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        variant: "default",
        title: t("components.dataTableCompanies.export.exportTitle"),
        description: t("components.dataTableCompanies.export.downloadStarting"),
      });
    } catch (error: any) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const showHubspot = hubspotIntegration?.isActive;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="hidden h-8 ml-auto lg:flex gap-2"
            disabled={fetchDataLoading || isLoading || companies.length === 0}
          >
            {isLoading ? (
              <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <DownloadIcon className="w-4 h-4" />
                {showHubspot && (
                  <img src={hubspotIcon} alt="HubSpot" className="w-4 h-4" />
                )}
              </>
            )}
            <span>{t("components.dataTableCompanies.export.exportList")}</span>
            <ChevronsUpDownIcon className="w-3.5 h-3.5 ml-0.5 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {showHubspot && (
            <>
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => setHubspotDialogOpen(true)}>
                  <img src={hubspotIcon} alt="HubSpot" className="w-4 h-4" />
                  <span>{t("components.dataTableCompanies.export.toHubspot")}</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => exportCompanies("csv")}>
              <FileSpreadsheetIcon className="w-4 h-4" />
              <span>{t("components.dataTableCompanies.export.formatCsv")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportCompanies("xlsx")}>
              <FileSpreadsheetIcon className="w-4 h-4" />
              <span>{t("components.dataTableCompanies.export.formatExcel")}</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {showHubspot && (
        <CompaniesHubspotListExport
          companies={companies}
          open={hubspotDialogOpen}
          onOpenChange={setHubspotDialogOpen}
          onCompaniesSynced={onCompaniesSynced}
        />
      )}
    </>
  );
}
